import { createRouter, createWebHistory } from 'vue-router'
import { store } from './../store'
import LoginLayout from '../layouts/Login.vue'
import WorkspaceLayout from '../modules/workspace/layouts/WorkspaceLayout.vue'

const routes = [
  {
    path: '/',
    component: WorkspaceLayout,
    children: [
      {
        path: '',
        name: 'Workspace',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ProjectSelect.vue')
      },
      {
        path: 'retrospective/:projectId',
        name: 'RetrospectiveDashboard',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/views/RetrospectiveDashboard.vue')
      },
      {
        path: 'prospective/:projectId',
        name: 'ProspectiveDashboard',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/views/ProspectiveDashboard.vue')
      },
      // Analytics route (tabbed component)
      {
        path: 'retrospective/:projectId/analytics',
        name: 'Analytics',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/Results/Analytics.vue')
      },
      {
        path: 'retrospective/:projectId/download-project',
        name: 'DownloadProject',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/DataAnnotations/DownloadProject.vue')
      },
      // AI Comparison route (tabbed component)
      {
        path: 'retrospective/:projectId/ai-comparison',
        name: 'AIComparison',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/Comparison/AIComparison.vue')
      },
      // AI Evaluation route (tabbed component)
      {
        path: 'prospective/:projectId/ai-evaluation',
        name: 'AIEvaluation',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/AIEvaluation/AIEvaluation.vue')
      },
      // Reader Evaluation route (tabbed component)
      {
        path: 'prospective/:projectId/reader-evaluation',
        name: 'ReaderEvaluation',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ReaderEvaluation/ReaderEvaluation.vue')
      },
      // Technical Evaluation route (tabbed component)
      {
        path: 'prospective/:projectId/technical-evaluation',
        name: 'TechnicalEvaluation',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/TechnicalEvaluation/TechnicalEvaluation.vue')
      },
      // Project Management routes
      {
        path: 'project/:projectId/blank',
        name: 'ProjectBlank',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/Blank.vue')
      },
      {
        path: 'project/:projectId/members',
        name: 'ProjectMembers',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ProjectManagement/Members.vue')
      },
      {
        path: 'project/:projectId/tags',
        name: 'ProjectTags',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ProjectManagement/TagsManager.vue')
      },
      {
        path: 'project/:projectId/pathologies',
        name: 'ProjectPathologies',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ProjectManagement/PathologiesManager.vue')
      },
      {
        path: 'project/:projectId/diagnoses',
        name: 'ProjectDiagnoses',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ProjectManagement/DiagnosesManager.vue')
      },
      {
        path: 'project/:projectId/subgroups',
        name: 'ProjectSubgroups',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ProjectManagement/SubgroupsManager.vue')
      },
      {
        path: 'project/:projectId/report-extraction',
        name: 'ReportExtraction',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ProjectManagement/ReportExtractionManager.vue')
      },
      // Project Data routes
      {
        path: 'project/:projectId/upload-images',
        name: 'UploadImages',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ProjectData/UploadImages.vue')
      },
      {
        path: 'project/:projectId/upload-metadata',
        name: 'UploadMetadata',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ProjectData/UploadMetadata.vue')
      },
      {
        path: 'project/:projectId/upload-reports',
        name: 'UploadReports',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/components/ProjectData/UploadReports.vue')
      },
      // Prospective Light routes
      {
        path: 'pros-light/:projectId',
        name: 'ProsLightDashboard',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/views/ProsLightDashboard.vue')
      },
      {
        path: 'pros-light/:projectId/ai-performance-analytics',
        name: 'AIPerformanceAnalytics',
        component: () => import(/* webpackChunkName: "workspace" */ '../modules/workspace/views/proslight/AIPerformanceAnalytics.vue')
      }
    ]
  },
  {
    path: '/login',
    component: LoginLayout,
    children: [
      {
        path: '',
        name: 'Login',
        component: () => import(/* webpackChunkName: "auth" */ '../views/Auth/Login.vue')
      },
      {
        path: 'response',
        name: 'LoginResponse',
        component: () => import(/* webpackChunkName: "auth" */ '../views/Auth/LoginResponse.vue')
      }
    ]
  }
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior () {
    return { left: 0, top: 0 }
  },
  routes
})

router.beforeEach(async (to, _from, next) => {
  window.scrollTo(0, 0)

  const publicPages = ['/forgot', '/login', '/register', '/p']
  let authRequired = !publicPages.includes(to.path)
  if (authRequired) {
    authRequired = to.path.indexOf('/l/') === -1 &&
                   to.path.indexOf('/p/') === -1 &&
                   to.path.indexOf('/r/') === -1 &&
                   to.path.indexOf('/login/') === -1 &&
                   to.path.indexOf('/register/') === -1
  }

  // Ensure auth check
  let user = store.state.auth.user
  const loggedOut = store.state.auth.loggedOut

  if (!user && !loggedOut) {
    try {
      await store.dispatch('auth/initAuth')
      await store.dispatch('usersSubmission/validateSession')
      user = store.state.auth.user
    } catch {
      // User not authenticated
    }
  }

  if (authRequired && !user) {
    return next('/login')
  }

  next()
})
